import {
  Bar,
  BarChart as RechartsBarChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

import type { ChartLabelFormat } from '../shared/types';

import {
  CHART_AXIS,
  CHART_CATEGORY_LABEL_LAYOUT,
  CHART_DIMENSIONS,
  CHART_VALUE_LABEL_TEXT,
} from '../shared/chart-tokens';
import type { ChartCategoryLabelMode } from '../shared/chart-tokens';
import { InvalidChart } from '../shared/invalid-chart';
import { getSeriesSignSummary } from '../shared/chart-helpers';
import { validateBarChartProps } from '../shared/chart-validation';
import { formatValue } from '../shared/format-value';

import { BarChartLegend } from './bar-chart-legend';
import { useAngledAxisMargins } from './bar-chart-axis-margins';
import { BarChartBar } from './bar-chart-bar';
import { BarChartValueLabel } from './bar-chart-value-label';
import { BarChartXAxisTick } from './bar-chart-x-axis-tick';
import { useAutoCategoryLabelMode } from './use-auto-category-label-mode';
import {
  BAR_CHART_CATEGORY_KEY,
  barChartSeriesKey,
  buildBarChartAriaLabel,
  resolveSeriesLabel,
  resolveSeriesValue,
  resolveTooltipSeriesLabel,
  toBarChartRows,
} from './bar-chart-data';

export interface BarChartData {
  categoryAxis: { label?: string; categories: readonly string[] };
  series: ReadonlyArray<{
    label?: string;
    values: ReadonlyArray<number | null>;
  }>;
  format?: ChartLabelFormat;
}

export interface BarChartProps {
  data: BarChartData;
  title?: string;
  animate?: boolean;
  className?: string;
  maxBarSize?: number;
  showValueLabels?: boolean;
  categoryLabelMode?: ChartCategoryLabelMode;
  cacheId?: string;
}

export const BarChart = ({
  data,
  title,
  animate = true,
  className,
  maxBarSize,
  showValueLabels = true,
  categoryLabelMode = 'truncate',
  cacheId,
}: BarChartProps) => {
  const validation = validateBarChartProps(data);
  const categories = validation.valid
    ? validation.normalized.categoryAxis.categories
    : ([] as readonly string[]);
  const truncateLayout = CHART_CATEGORY_LABEL_LAYOUT.truncate;
  const { chartRootRef, chartRootElement, effectiveLabelMode } =
    useAutoCategoryLabelMode({
      categoryLabelMode,
      categories,
      isValid: validation.valid,
      cacheId,
    });

  const { marginLeft: appliedMarginLeft, marginBottom: appliedMarginBottom } =
    useAngledAxisMargins({
      categoryLabelMode: effectiveLabelMode,
      categories,
      isValid: validation.valid,
      chartRootElement,
      cacheId,
    });
  const modeLayout = CHART_CATEGORY_LABEL_LAYOUT[effectiveLabelMode];
  const containerHeight =
    CHART_DIMENSIONS.defaultHeight +
    (modeLayout.xAxisLabelHeight - truncateLayout.xAxisLabelHeight) +
    (appliedMarginBottom - truncateLayout.marginBottom);

  if (!validation.valid) {
    return <InvalidChart className={className} />;
  }

  const { normalized } = validation;
  const signSummary = getSeriesSignSummary(normalized.series);
  const dataCrossesZero = signSummary.hasPositive && signSummary.hasNegative;
  const chartData = toBarChartRows(normalized);
  const seriesCount = normalized.series.length;

  return (
    <div ref={chartRootRef} className={className}>
      <BarChartLegend series={normalized.series} className="mt-3" />
      <ResponsiveContainer width="100%" height={containerHeight}>
        <RechartsBarChart
          data={chartData}
          margin={{
            top: CHART_DIMENSIONS.marginTop,
            right: modeLayout.marginRight,
            left: appliedMarginLeft,
            bottom: appliedMarginBottom,
          }}
          barGap={CHART_DIMENSIONS.barGap}
          accessibilityLayer={true}
          aria-label={buildBarChartAriaLabel(normalized, title)}
        >
          {/* Screen readers announce bar values during keyboard navigation. */}
          <Tooltip
            trigger="click"
            formatter={(value, _name, item) =>
              resolveTooltipSeriesLabel(value, item, normalized.format)
            }
            wrapperClassName="sr-only"
            cursor={false}
          />
          <XAxis
            dataKey={BAR_CHART_CATEGORY_KEY}
            axisLine={false}
            tickLine={false}
            interval={0}
            height={modeLayout.xAxisLabelHeight}
            tick={
              <BarChartXAxisTick
                categoryLabelMode={effectiveLabelMode}
                includeTruncateProbe={categoryLabelMode === 'auto'}
              />
            }
          />
          <YAxis
            hide={showValueLabels}
            axisLine={false}
            tickLine={false}
            width="auto"
            tickFormatter={(tick: number) =>
              formatValue(tick, normalized.format)
            }
            padding={{
              top: CHART_DIMENSIONS.yAxisLabelPaddingTop,
              bottom: dataCrossesZero
                ? CHART_DIMENSIONS.yAxisLabelPaddingBottom
                : 0,
            }}
            tick={{
              fill: CHART_AXIS.tickColor,
              fontFamily: CHART_AXIS.tickFontFamily,
              fontSize: CHART_AXIS.tickFontSize,
            }}
          />
          {dataCrossesZero && (
            <ReferenceLine y={0} stroke={CHART_AXIS.zeroLineColor} />
          )}
          {normalized.series.map((singleSeries, seriesIndex) => {
            const dataKey = barChartSeriesKey(seriesIndex);

            return (
              <Bar
                key={dataKey}
                dataKey={dataKey}
                name={singleSeries.label}
                maxBarSize={maxBarSize}
                isAnimationActive={animate}
                shape={(shapeProps) => {
                  const { value, payload, ...rest } = shapeProps;

                  return (
                    <BarChartBar
                      {...rest}
                      value={resolveSeriesValue(
                        { value, payload },
                        seriesIndex
                      )}
                      seriesIndex={seriesIndex}
                      seriesCount={seriesCount}
                    />
                  );
                }}
              >
                {showValueLabels && (
                  <LabelList
                    valueAccessor={(entry) =>
                      resolveSeriesLabel(entry, seriesIndex, normalized.format)
                    }
                    position="top"
                    content={BarChartValueLabel}
                    {...CHART_VALUE_LABEL_TEXT}
                  />
                )}
              </Bar>
            );
          })}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};
